/**
 * ルーム（Rooms API）サービス。
 *
 * Hub API: `/games/:gameId/rooms/...` 系エンドポイントのラッパー。
 *   - POST   /rooms                         — ルーム作成（作成者がホスト）
 *   - GET    /rooms/:roomId                 — ルーム情報取得
 *   - POST   /rooms/:roomId/invites         — 招待コード発行
 *   - POST   /rooms/joinByInvite            — 招待コードでルーム参加
 *   - POST   /rooms/:roomId/leave           — 退出
 *
 * T15 で追加使用予定:
 *   - POST /rooms/:roomId/ws-ticket         — WebSocket 一時チケット
 *   - GET  /rooms/:roomId/realtime-capabilities
 *   - POST /rooms/:roomId/realtime-state/commands
 *   を追加する。本タスクでは対戦ロビーへの遷移までが範囲。
 *
 * SDK メソッド → ymgFetch フォールバックパターン（他サービスと同様）。
 * エラーは catch せずに上流へ伝播させる（サイレントフォールバック禁止）。
 */

import { getYmgSdk, getGameId, ymgFetch, hasCapacitorSessionToken } from './sdk'
import { getAuthState } from './auth'
import {
  MATCH_MODE_DEFAULT,
  ROOM_CAPACITY,
  ROOM_TTL_SEC,
  type InviteCode,
  type RoomInfo,
  type RoomMemberInfo,
} from '../config/multiplayer'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CreateRoomParams {
  mode?: string
  capacity?: number
  region?: string
  build?: string
  metadata?: Record<string, unknown>
  expiresInSec?: number
}

// ---------------------------------------------------------------------------
// Normalizer
// ---------------------------------------------------------------------------

/**
 * Hub の YmgRoom を UI 用 RoomInfo に変換する。
 * members の ready が optional なので false 埋めする。
 *
 * matchmaking.ts からも import されるので、ここで公開する。
 */
export function normalizeRoom(room: YmgRoom): RoomInfo {
  const rawMembers = room.members ?? []
  const members: RoomMemberInfo[] = new Array(rawMembers.length)
  for (let i = 0; i < rawMembers.length; i++) {
    const m = rawMembers[i]
    members[i] = {
      userId: m.userId,
      joinedAt: m.joinedAt,
      ready: m.ready ?? false,
    }
  }
  return {
    roomId: room.roomId,
    gameId: room.gameId,
    platformId: room.platformId,
    mode: room.mode,
    capacity: room.capacity,
    hostUserId: room.hostUserId,
    members,
    state: room.state,
    metadata: room.metadata,
    createdAt: room.createdAt,
    expiresAt: room.expiresAt,
  }
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export async function createRoom(params: CreateRoomParams = {}): Promise<RoomInfo> {
  const gameId = getGameId()
  const body = {
    mode: params.mode ?? MATCH_MODE_DEFAULT,
    capacity: params.capacity ?? ROOM_CAPACITY,
    region: params.region,
    build: params.build,
    metadata: params.metadata,
    expiresInSec: params.expiresInSec ?? ROOM_TTL_SEC,
  }

  const preferFetch = getAuthState().status === 'signed_in' || (await hasCapacitorSessionToken())

  // signed_in: ymgFetch 一本（重複エンドポイント呼び出しによる二重ルーム作成防止）
  if (preferFetch) {
    const res = await ymgFetch<YmgRoomResponse>(
      `/games/${encodeURIComponent(gameId)}/rooms`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    )
    return normalizeRoom(res.room)
  }

  const sdk = await getYmgSdk()
  const sdkApi = sdk.api as unknown as {
    createRoom?: (p: {
      gameId: string
      mode: string
      capacity: number
      region?: string
      build?: string
      metadata?: Record<string, unknown>
      expiresInSec?: number
    }) => Promise<YmgRoomResponse>
  }
  if (typeof sdkApi.createRoom === 'function') {
    const res = await sdkApi.createRoom({ gameId, ...body })
    return normalizeRoom(res.room)
  }

  const res = await ymgFetch<YmgRoomResponse>(
    `/games/${encodeURIComponent(gameId)}/rooms`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  )
  return normalizeRoom(res.room)
}

export async function getRoomInfo(roomId: string): Promise<RoomInfo> {
  const gameId = getGameId()
  const preferFetch = getAuthState().status === 'signed_in' || (await hasCapacitorSessionToken())

  if (preferFetch) {
    const res = await ymgFetch<YmgRoomResponse>(
      `/games/${encodeURIComponent(gameId)}/rooms/${encodeURIComponent(roomId)}`,
      { method: 'GET' },
    )
    return normalizeRoom(res.room)
  }

  const sdk = await getYmgSdk()
  const sdkApi = sdk.api as unknown as {
    getRoom?: (p: { gameId: string; roomId: string }) => Promise<YmgRoomResponse>
  }
  if (typeof sdkApi.getRoom === 'function') {
    const res = await sdkApi.getRoom({ gameId, roomId })
    return normalizeRoom(res.room)
  }

  const res = await ymgFetch<YmgRoomResponse>(
    `/games/${encodeURIComponent(gameId)}/rooms/${encodeURIComponent(roomId)}`,
    { method: 'GET' },
  )
  return normalizeRoom(res.room)
}

export async function createInvite(roomId: string): Promise<InviteCode> {
  const gameId = getGameId()
  const preferFetch = getAuthState().status === 'signed_in' || (await hasCapacitorSessionToken())

  if (preferFetch) {
    const res = await ymgFetch<YmgRoomInviteResponse>(
      `/games/${encodeURIComponent(gameId)}/rooms/${encodeURIComponent(roomId)}/invites`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      },
    )
    return { code: res.code, expiresAt: res.expiresAt }
  }

  const sdk = await getYmgSdk()
  const sdkApi = sdk.api as unknown as {
    createRoomInvite?: (p: {
      gameId: string
      roomId: string
    }) => Promise<YmgRoomInviteResponse>
  }
  if (typeof sdkApi.createRoomInvite === 'function') {
    const res = await sdkApi.createRoomInvite({ gameId, roomId })
    return { code: res.code, expiresAt: res.expiresAt }
  }

  const res = await ymgFetch<YmgRoomInviteResponse>(
    `/games/${encodeURIComponent(gameId)}/rooms/${encodeURIComponent(roomId)}/invites`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    },
  )
  return { code: res.code, expiresAt: res.expiresAt }
}

export async function joinByInvite(
  code: string,
): Promise<{ room: RoomInfo; joined: boolean }> {
  const gameId = getGameId()
  const preferFetch = getAuthState().status === 'signed_in' || (await hasCapacitorSessionToken())

  if (preferFetch) {
    const res = await ymgFetch<YmgJoinRoomResponse>(
      `/games/${encodeURIComponent(gameId)}/rooms/joinByInvite`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      },
    )
    return { room: normalizeRoom(res.room), joined: res.joined }
  }

  const sdk = await getYmgSdk()
  const sdkApi = sdk.api as unknown as {
    joinRoomByInvite?: (p: {
      gameId: string
      code: string
    }) => Promise<YmgJoinRoomResponse>
  }
  if (typeof sdkApi.joinRoomByInvite === 'function') {
    const res = await sdkApi.joinRoomByInvite({ gameId, code })
    return { room: normalizeRoom(res.room), joined: res.joined }
  }

  const res = await ymgFetch<YmgJoinRoomResponse>(
    `/games/${encodeURIComponent(gameId)}/rooms/joinByInvite`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    },
  )
  return { room: normalizeRoom(res.room), joined: res.joined }
}

/**
 * ルームから退出する。ホストが退出するとルームは CLOSED になる（Hub 仕様）。
 *
 * 冪等性: 404（既にルームが存在しない）は成功扱いで swallow。
 * それ以外のエラーは上流に伝播する。
 */
export async function leaveRoom(roomId: string): Promise<void> {
  const gameId = getGameId()
  const preferFetch = getAuthState().status === 'signed_in' || (await hasCapacitorSessionToken())

  try {
    if (preferFetch) {
      await ymgFetch<{ ok: boolean }>(
        `/games/${encodeURIComponent(gameId)}/rooms/${encodeURIComponent(roomId)}/leave`,
        { method: 'POST' },
      )
      return
    }
    const sdk = await getYmgSdk()
    const sdkApi = sdk.api as unknown as {
      leaveRoom?: (p: { gameId: string; roomId: string }) => Promise<{ ok: boolean }>
    }
    if (typeof sdkApi.leaveRoom === 'function') {
      await sdkApi.leaveRoom({ gameId, roomId })
      return
    }
    await ymgFetch<{ ok: boolean }>(
      `/games/${encodeURIComponent(gameId)}/rooms/${encodeURIComponent(roomId)}/leave`,
      { method: 'POST' },
    )
  } catch (err) {
    const status = (err as { status?: number } | null)?.status
    if (status === 404) return
    throw err
  }
}
